import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import { useApiClient } from '../lib/use-api-client.js';

/**
 * Hooks for the per-tenant secret-storage operator surface introduced
 * in 2026-05. Five entity types (`client` / `license` / `oauth-app` /
 * `workfront-config` / `fusion-config`) × three ops (list / set /
 * delete) — but each hook is parameterised by `bundlePath` so all five
 * entity types share the same query implementation.
 *
 * `bundlePath` is the API path **up to but not including**
 * `/secret-fields` — e.g. `/v1/clients/{cid}` or
 * `/v1/clients/{cid}/licenses/{lid}`. Callers compose this string from
 * route params + the entity row's id.
 */

interface SecretFieldsListResponse {
  fields: string[];
}

interface SecretFieldSetResponse {
  ok: true;
}

interface SecretFieldDeleteResponse {
  ok: true;
  removed: boolean;
}

/**
 * Stable TanStack Query key for a bundle's secret-fields list. The
 * mutations below invalidate this key after a successful write so the
 * "field is set" indicators refresh.
 */
export function secretFieldsQueryKey(bundlePath: string): readonly unknown[] {
  return ['secret-fields', bundlePath];
}

export function useSecretFieldsQuery(
  bundlePath: string,
  enabled = true,
): UseQueryResult<string[], Error> {
  const api = useApiClient();
  return useQuery({
    queryKey: secretFieldsQueryKey(bundlePath),
    enabled: enabled && Boolean(api),
    queryFn: async () => {
      if (!api) throw new Error('useSecretFieldsQuery: api client unavailable');
      const r = await api.get<SecretFieldsListResponse>(`${bundlePath}/secret-fields`);
      return r.fields;
    },
  });
}

interface SetSecretInput {
  bundlePath: string;
  field: string;
  value: string;
}

export function useSetSecretFieldMutation(): UseMutationResult<
  SecretFieldSetResponse,
  Error,
  SetSecretInput
> {
  const api = useApiClient();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ bundlePath, field, value }) => {
      if (!api) throw new Error('useSetSecretFieldMutation: api client unavailable');
      return api.put<SecretFieldSetResponse>(
        `${bundlePath}/secret-fields/${encodeURIComponent(field)}`,
        { value },
      );
    },
    onSuccess: (_data, { bundlePath }) => {
      void qc.invalidateQueries({ queryKey: secretFieldsQueryKey(bundlePath) });
    },
  });
}

interface DeleteSecretInput {
  bundlePath: string;
  field: string;
}

export function useDeleteSecretFieldMutation(): UseMutationResult<
  SecretFieldDeleteResponse,
  Error,
  DeleteSecretInput
> {
  const api = useApiClient();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ bundlePath, field }) => {
      if (!api) throw new Error('useDeleteSecretFieldMutation: api client unavailable');
      return api.delete<SecretFieldDeleteResponse>(
        `${bundlePath}/secret-fields/${encodeURIComponent(field)}`,
      );
    },
    onSuccess: (_data, { bundlePath }) => {
      void qc.invalidateQueries({ queryKey: secretFieldsQueryKey(bundlePath) });
    },
  });
}
